/*
 * What a group picker offers, worked out once from the caller's candidates (`GroupPicker.tsx`).
 *
 * The picker never decides *which* groups may be chosen — that is a policy of the thing being owned,
 * and the caller hands the list over — but it does decide how the list reads. Groups and people are
 * two lists, because a person looks for "the designers" and for "Anna" in different places: the
 * groups start with the special groups in the directory's order (Everyone, Team members,
 * Administrators — whichever the caller offers), then the ones the reader is in, and the people are
 * the one-person groups, drawn as their person, the reader's own first as "Only me". Everything here
 * is a pure function of the candidates, the accounts and the reader, so it is tested without a
 * screen.
 */

import { t } from "../i18n/index.ts"
import { matchesPerson } from "../people/person.ts"
import type { GroupId, GroupView, PublicUser, UserId } from "../store/model.ts"
import { byGroupName, groupName, isPersonalGroup, isSpecialGroup, isTeamGroup } from "./group.ts"

/** One row a picker can offer. */
export interface GroupOption {
	group: GroupView
	/** What the row is called: the group's name, "Only me", or the person's name. */
	label: string
	/** The account a personal group stands for, when the reader's model knows it. */
	person?: PublicUser | undefined
	/** Whether the reader belongs to the group — the membership the server would count. */
	holdsReader: boolean
}

export interface GroupOptions {
	/** The team members group, when the caller offers it. */
	team?: GroupOption | undefined
	/** The reader's own personal group, when the caller offers it. */
	mine?: GroupOption | undefined
	/** Every offered group that is not personal: the specials first, then the reader's, then the rest. */
	groups: GroupOption[]
	/** Every offered personal group: the reader's first, then the others by the person's name. */
	people: GroupOption[]
	/** Every option by group id, for drawing what is chosen. */
	byId: ReadonlyMap<GroupId, GroupOption>
}

function personalOption(
	group: GroupView,
	users: Readonly<Record<UserId, PublicUser>>,
	reader: UserId,
): GroupOption {
	const owner = group.personalUserId
	const person = owner === undefined ? undefined : users[owner]
	const mine = owner === reader
	return {
		group,
		label: mine ? t("groups.choose.me") : (person?.displayName ?? group.displayName),
		person,
		holdsReader: mine,
	}
}

export function groupOptions(
	candidates: readonly GroupView[],
	users: Readonly<Record<UserId, PublicUser>>,
	reader: UserId,
): GroupOptions {
	const groups: GroupOption[] = []
	const people: GroupOption[] = []
	for (const group of candidates) {
		if (isPersonalGroup(group)) people.push(personalOption(group, users, reader))
		else {
			groups.push({
				group,
				label: groupName(group),
				holdsReader: group.memberIds.includes(reader),
			})
		}
	}

	groups.sort(
		(a, b) =>
			Number(isSpecialGroup(b.group)) - Number(isSpecialGroup(a.group)) ||
			(isSpecialGroup(a.group) ? 0 : Number(b.holdsReader) - Number(a.holdsReader)) ||
			byGroupName(a.group, b.group),
	)
	const mine = people.find((option) => option.holdsReader)
	const others = people.filter((option) => option !== mine)
	// The directory's order for people (`person.ts` byName): the name, with the handle deciding a tie.
	others.sort(
		(a, b) =>
			a.label.localeCompare(b.label) ||
			(a.person?.handle ?? "").localeCompare(b.person?.handle ?? ""),
	)

	const all = [...groups, ...(mine === undefined ? [] : [mine]), ...others]
	return {
		team: groups.find((option) => isTeamGroup(option.group)),
		mine,
		groups,
		people: mine === undefined ? others : [mine, ...others],
		byId: new Map(all.map((option) => [option.group.id, option])),
	}
}

/** Whether a row matches what was typed: a group by its names, a person by theirs. */
export function matchesOption(option: GroupOption, query: string): boolean {
	const needle = query.trim().toLowerCase().replace(/^@/, "")
	if (needle === "") return true
	if (option.person !== undefined && matchesPerson(option.person, query)) return true
	return (
		option.label.toLowerCase().includes(needle) ||
		(!isPersonalGroup(option.group) && option.group.handle.toLowerCase().includes(needle))
	)
}

/** Which of the prominent answers a chosen set is, if it is one of them. */
export type Preset = "team" | "mine" | "custom" | "none"

export function presetOf(value: readonly GroupId[], options: GroupOptions): Preset {
	if (value.length === 0) return "none"
	if (value.length === 1) {
		if (value[0] === options.team?.group.id) return "team"
		if (value[0] === options.mine?.group.id) return "mine"
	}
	return "custom"
}
